// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — PierQuestBoard.jsx   ·   THE REP AT THE DOOR
// ────────────────────────────────────────────────────────────────────
// One overlay, opened from a game portal. It shows who is standing
// there, what work they have, and what you are already carrying.
//
// ⚑ IT OWNS ITS OWN SUBSCRIPTIONS ON PURPOSE. UnstablePierre.js is a
// 143 KB live file that every other system on the pier already runs
// through, and the cheapest possible change to it is one that adds a
// mount and nothing else. So this component reads its own rates, its own
// wallet, its own achievements and its own quest rows, and the host
// hands it a uid and a game id. The blast radius of the whole feature on
// that file is a button and a conditional.
// ════════════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo } from "react";
import { SD, UI, uiPanel, uiBtn, uiLabel } from "./theme";
import { listenToRates, listenToWallet } from "./progression";
import { useAchievements } from "./useAchievements";
import { GAME_NAMESPACES } from "./achievements";
import { currencyMeta } from "./ieEconomy";
import {
  repFor, boardFor, listenToQuests, acceptQuest, abandonQuest, turnInQuest, questProgress,
} from "./pierQuests";

const UD = "✹";
// ⚠ This file IMPORTED the theme and then ignored it — a `MONO` const
// of its own, a `GREEN` of its own, `borderRadius: 3` against the
// product's 8, and four separate hand-rolled dim greys, every one of
// them under 3.6:1 on this panel. Importing a design system and not
// using it is worse than not importing it, because the next reader
// assumes the file is already in the system.
const MONO = UI.font;
const GREEN = UI.ok;
const fmt = (n) => (n || 0).toLocaleString();

// ⚠ SAME NAME, SAME ORDER, SAME MEANING AS EVERY OTHER `btn` WRAPPER.
// This one was `(tint, disabled)` with `on: !disabled` — the opposite
// argument order from MineriumScreen's, and it rendered EVERY enabled
// button in the selected state, which theme.js defines as "selected, not
// hover". "TAKE THE JOB" and a toggled-on control looked identical.
const btn = (on, tint = UI.gold, disabled = false) =>
  ({ ...uiBtn(tint, { on, disabled }), fontWeight: 700 });
const cap = uiLabel;

export default function PierQuestBoard({ gameId, uid, onClose }) {
  const [rates, setRates]   = useState(null);
  const [wallet, setWallet] = useState({});
  const [rows, setRows]     = useState({});
  const [busy, setBusy]     = useState(false);
  const [flash, setFlash]   = useState(null);
  const { map: achievementMap } = useAchievements(uid);

  useEffect(() => listenToRates(setRates), []);
  useEffect(() => listenToWallet(uid, setWallet), [uid]);
  useEffect(() => listenToQuests(uid, setRows), [uid]);

  const rep = repFor(gameId);
  const game = GAME_NAMESPACES[gameId] || { label: gameId, tint: SD.gold, icon: "◈" };
  const tint = game.tint || SD.gold;

  const board = useMemo(
    () => boardFor(gameId, { rates, achievementMap, questRows: rows }),
    [gameId, rates, achievementMap, rows]
  );

  const say = (ok, text) => { setFlash({ ok, text }); setTimeout(() => setFlash(null), 3600); };

  const onAccept = async (q) => {
    setBusy(true);
    try {
      const r = await acceptQuest(uid, q, rates);
      say(!!r.ok, r.ok ? "Taken. Go and do it." : r.message);
    } finally { setBusy(false); }
  };

  const onTurnIn = async (q) => {
    setBusy(true);
    try {
      // ⚠ NO `ctx`. `turnInQuest` reads its own evidence out of the
      // database now — it used to accept this object and pay against it,
      // which meant any caller could describe itself as finished and be
      // paid. See `liveEvidence` in pierQuests.js.
      // against live values before it pays anything. A client that says
      // a job is done is describing what it WANTS to be paid for.
      const r = await turnInQuest(uid, q.id);
      say(!!r.ok, r.ok ? `+${UD}${fmt(r.paidUD)}${r.partial ? " (partial)" : ""}` : r.message);
    } finally { setBusy(false); }
  };

  const onDrop = async (q) => {
    setBusy(true);
    try { await abandonQuest(uid, q.id); say(true, "Put back on the board."); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 260,
      background: "radial-gradient(ellipse at 50% 40%, rgba(15,8,40,0.86) 0%, rgba(0,0,0,0.93) 75%)",
      backdropFilter: "blur(3px)", display: "flex", alignItems: "center",
      justifyContent: "center", padding: 18, fontFamily: MONO,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        ...uiPanel(tint),
        width: "min(660px, 100%)", maxHeight: "86vh", display: "flex", flexDirection: "column",
      }}>
        {/* ── the rep ─────────────────────────────────────────────── */}
        <div style={{ padding: "15px 18px", borderBottom: `1px solid ${tint}33` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22 }}>{game.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: ".1em", color: tint }}>
                {rep.name.toUpperCase()}
              </div>
              <div style={{ ...cap, marginTop: 3 }}>
                {String(game.label).toUpperCase()} · AT THE DOOR
              </div>
            </div>
            <button onClick={onClose} style={btn(false, UI.dim)}>CLOSE</button>
          </div>
          <div style={{ fontSize: 10.5, color: UI.dim, marginTop: 10, fontStyle: "italic", lineHeight: 1.6 }}>
            “{rep.line}”
          </div>
          {/* ⚑ SAID OUT LOUD, because it is the design and a player
              should understand why the money is what it is. */}
          <div style={{ fontSize: 9, color: UI.dim, marginTop: 8, lineHeight: 1.6 }}>
            Work is paid in {UD} Dawgs by the Pier, never by {game.label} —
            they keep their own books.
          </div>
        </div>

        {flash && (
          <div style={{
            padding: "8px 18px", fontSize: 10.5,
            color: flash.ok ? GREEN : UI.err,
            background: flash.ok ? "#101a0a" : "#1a0c0c",
            borderBottom: `1px solid ${flash.ok ? UI.edge : "#3a2020"}`,
          }}>{flash.text}</div>
        )}

        {/* ── the board ───────────────────────────────────────────── */}
        <div style={{ overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 9 }}>
          {rates === null && <div style={{ fontSize: 10, color: UI.dim, padding: 10 }}>Reading the books…</div>}

          {rates !== null && board.length === 0 && (
            <div style={{ fontSize: 10.5, color: UI.dim, padding: 12, lineHeight: 1.7 }}>
              Nothing on the board. Either you have done it all, or nobody has written
              this one's work up yet — one row in <span style={{ color: tint }}>pierQuests.js</span>
              {" "}and they are open for business.
            </div>
          )}

          {board.map((q) => {
            const prog = q.row ? questProgress(q.row, { balances: wallet, achievementMap }) : null;
            const taken = q.row?.status === "accepted";
            const cur = q.currency ? currencyMeta(q.currency, rates) : null;
            return (
              <div key={q.id} style={{
                border: `1px solid ${taken ? `${tint}55` : UI.edge}`, borderRadius: 5,
                padding: 12, background: taken ? `${tint}10` : UI.panelSolid,
              }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: UI.text }}>{q.name}</span>
                  {q.rarity && (
                    <span style={{ fontSize: 8, letterSpacing: ".16em", color: q.rarityTint || UI.dim }}>
                      {String(q.rarity).toUpperCase()}
                    </span>
                  )}
                  {q.repeatable && (
                    <span style={{ ...cap, fontSize: 8.5 }}>REPEATABLE</span>
                  )}
                  <span style={{ flex: 1 }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: GREEN }}>{UD}{fmt(q.rewardUD)}</span>
                </div>

                <div style={{ fontSize: 10, color: UI.dim, marginTop: 7, lineHeight: 1.6 }}>{q.ask}</div>

                {taken && prog && (
                  <div style={{ marginTop: 9 }}>
                    <div style={{ height: 4, borderRadius: 2, background: UI.edge, overflow: "hidden" }}>
                      <div style={{
                        height: "100%", borderRadius: 2,
                        width: `${Math.min(100, (prog.have / Math.max(1, prog.need)) * 100)}%`,
                        background: prog.done ? GREEN : tint,
                      }} />
                    </div>
                    <div style={{ fontSize: 9, color: prog.done ? GREEN : UI.dim, marginTop: 5 }}>
                      {q.kind === "earn"
                        ? `${fmt(prog.have)} / ${fmt(prog.need)} ${cur?.icon || ""} since you took it`
                        : prog.done ? "They have marked you." : "Not yet marked."}
                    </div>
                  </div>
                )}

                <div style={{ display: "flex", gap: 7, marginTop: 11 }}>
                  {!taken && (
                    <button disabled={busy} onClick={() => onAccept(q)} style={btn(true, tint, busy)}>TAKE THE JOB</button>
                  )}
                  {taken && (
                    <>
                      <button disabled={busy || !prog?.done} onClick={() => onTurnIn(q)}
                              style={btn(true, GREEN, busy || !prog?.done)}>
                        {prog?.done ? "COLLECT" : "NOT DONE"}
                      </button>
                      <button disabled={busy} onClick={() => onDrop(q)} style={btn(false, UI.dim, busy)}>GIVE IT BACK</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
